/** Eksportene under «agency» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/agency/`. */
import type { ExportColumnMeta, ExportMeta, ExportSectionMeta } from '../types.js';

const col = (
  group: string,
  key: string,
  type: ExportColumnMeta['type'],
  extra: Omit<ExportColumnMeta, 'key' | 'labelKey' | 'type'> = {},
): ExportColumnMeta => ({
  key,
  labelKey: `export.agency.${group}.columns.${key}`,
  type,
  ...extra,
});

/** 250.19: honoraret følger beløpsregelen, som dialogen også skjuler det med. */
const amount = { rule: 'booking_amounts' } as const;

const hidden = { defaultHidden: true } as const;

/**
 * STALLEN: tabellen på `/artists` (fanen Artister) med søket, «Vis arkiverte», sorteringen og de
 * kolonnene leseren har valgt (`tableKey: 'agency-artists'`, kolonnenøklene er skjermens). Honoraret
 * er standardformatets og tas ut uten `canSeeBookingAmounts`; provisjonssatsen krever avtalens celle.
 */
const ARTISTS: ExportMeta = {
  id: 'agency.artists',
  domain: 'agency',
  kind: 'table',
  titleKey: 'export.agency.artists.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  tableKey: 'agency-artists',
  columns: [
    col('artists', 'name', 'text'),
    col('artists', 'genre', 'text'),
    col('artists', 'homeTown', 'text'),
    col('artists', 'formats', 'text'),
    col('artists', 'fee', 'money', amount),
    col('artists', 'assignee', 'text'),
    col('artists', 'commissionBooking', 'decimal', {
      ...hidden,
      requires: ['artist_agreement:read'],
    }),
    col('artists', 'artistType', 'text', hidden),
    col('artists', 'partySize', 'integer', hidden),
    col('artists', 'frontPerson', 'text', hidden),
    col('artists', 'gender', 'text', hidden),
    col('artists', 'ageGroup', 'text', hidden),
    col('artists', 'spotifyUrl', 'text', hidden),
    col('artists', 'instagramHandle', 'text', hidden),
    col('artists', 'instagramFollowers', 'integer', hidden),
    col('artists', 'monthlyListeners', 'integer', hidden),
    col('artists', 'archivedAt', 'date', hidden),
    col('artists', 'createdAt', 'date', hidden),
    col('artists', 'updatedAt', 'date', hidden),
  ],
  landscape: true,
};

const section = (key: string, extra: Omit<ExportSectionMeta, 'key' | 'labelKey'> = {}) => ({
  key,
  labelKey: `export.agency.artistProfile.sections.${key}`,
  ...extra,
});

/**
 * ARTISTPROFILEN: ett dokument per artist, satt sammen av det artistsiden viser — stamdata, kontakter,
 * bandet, formatene (prisene bare der leseren ser beløp), dokumentene, kalenderens utilgjengelige
 * dager og notater, avtalen og sperreperiodene (bare med `artist_agreement:read`) og byråets notater.
 * Fra artistsiden (`object`), og fra stallen for merkede (`selection`) eller alle i filteret.
 */
const ARTIST_PROFILE: ExportMeta = {
  id: 'agency.artist-profile',
  domain: 'agency',
  kind: 'document',
  titleKey: 'export.agency.artistProfile.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  sections: [
    section('identity'),
    section('contacts'),
    section('members'),
    section('formats'),
    section('documents'),
    section('calendar'),
    section('agreement', { requires: ['artist_agreement:read'] }),
    section('blackouts', { requires: ['artist_agreement:read'] }),
    section('notes'),
  ],
};

/**
 * FORESPØRSLER OG BUD: innboksen på `/artists?tab=enquiries` med statusen, «Venter på oss», søket og
 * arrangøren (`ListBookingEnquiriesQuery` uten side), sorteringen og kolonnene
 * (`tableKey: 'agency-enquiries'`). Beløpet er null uten `canSeeBookingAmounts`, og da er kolonnen ute.
 */
const ENQUIRIES: ExportMeta = {
  id: 'agency.enquiries',
  domain: 'agency',
  kind: 'table',
  titleKey: 'export.agency.enquiries.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['performance:list'],
  guest: false,
  tableKey: 'agency-enquiries',
  columns: [
    col('enquiries', 'artist', 'text'),
    col('enquiries', 'from', 'text'),
    col('enquiries', 'date', 'date'),
    col('enquiries', 'venue', 'text'),
    col('enquiries', 'fee', 'money', amount),
    col('enquiries', 'availability', 'status'),
    col('enquiries', 'assignee', 'text'),
    col('enquiries', 'kind', 'status', hidden),
    col('enquiries', 'status', 'status', hidden),
    col('enquiries', 'format', 'text', hidden),
    col('enquiries', 'stage', 'text', hidden),
    col('enquiries', 'time', 'time', hidden),
    col('enquiries', 'respondsBy', 'date', hidden),
    col('enquiries', 'message', 'multiline', hidden),
    col('enquiries', 'answerNote', 'multiline', hidden),
    col('enquiries', 'answeredAt', 'date', hidden),
    col('enquiries', 'partySharedAt', 'date', hidden),
    col('enquiries', 'archivedAt', 'date', hidden),
    col('enquiries', 'createdAt', 'date', hidden),
  ],
  landscape: true,
};

/**
 * BOOKINGENE TIL ÉN ARTIST: fanen Bookinger på artistsiden — giggene hos arrangørene, budene og
 * forespørslene, byråets egne bookinger og byråets egne konserter, slått sammen som på skjermen
 * (`buildEngagements`), med fanens filter (solgt/egen, kommende/tidligere/uten dato, søket) og
 * kolonnene (`tableKey: 'artist-bookings'`).
 */
const ARTIST_BOOKINGS: ExportMeta = {
  id: 'agency.artist-bookings',
  domain: 'agency',
  kind: 'table',
  titleKey: 'export.agency.artistBookings.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['performance:list'],
  guest: false,
  tableKey: 'artist-bookings',
  columns: [
    col('artistBookings', 'date', 'date'),
    col('artistBookings', 'who', 'text'),
    col('artistBookings', 'where', 'text'),
    col('artistBookings', 'type', 'text'),
    col('artistBookings', 'format', 'text'),
    col('artistBookings', 'state', 'status'),
    col('artistBookings', 'fee', 'money', amount),
    col('artistBookings', 'breakEven', 'integer'),
    col('artistBookings', 'advance', 'status'),
    col('artistBookings', 'endsOn', 'date', hidden),
    col('artistBookings', 'terms', 'multiline', hidden),
    col('artistBookings', 'stage', 'text', hidden),
    col('artistBookings', 'time', 'time', hidden),
    col('artistBookings', 'respondsBy', 'date', hidden),
    col('artistBookings', 'assignee', 'text', hidden),
    col('artistBookings', 'message', 'multiline', hidden),
    col('artistBookings', 'createdAt', 'date', hidden),
  ],
  params: ['artistId'],
  landscape: true,
};

/**
 * PROVISJON OG AVTALER: én rad per artist med avtalen (Avtale-fanen) og årets provisjon slik fanen
 * regner den (`/commission`). Krever det fanen krever (`artist_agreement:read`, bare byråer), og er
 * aldri for gjester. Fra stallen (merkede eller filteret) og fra Avtale-fanen (`object`).
 */
const AGREEMENTS: ExportMeta = {
  id: 'agency.agreements',
  domain: 'agency',
  kind: 'table',
  titleKey: 'export.agency.agreements.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'selection', 'filter'],
  requires: ['performance:list', 'artist_agreement:read'],
  guest: false,
  columns: [
    col('agreements', 'artist', 'text'),
    col('agreements', 'role', 'status'),
    col('agreements', 'territory', 'text'),
    col('agreements', 'exclusive', 'boolean'),
    col('agreements', 'startsOn', 'date'),
    col('agreements', 'endsOn', 'date'),
    col('agreements', 'commissionBooking', 'decimal'),
    col('agreements', 'commissionManagement', 'decimal'),
    col('agreements', 'commissionBasis', 'status'),
    col('agreements', 'rate', 'decimal'),
    col('agreements', 'yearFee', 'money'),
    col('agreements', 'yearCommission', 'money'),
    col('agreements', 'yearBookings', 'integer'),
    col('agreements', 'rateSource', 'status', hidden),
    col('agreements', 'minimumFee', 'money', hidden),
    col('agreements', 'territoryExceptions', 'multiline', hidden),
    col('agreements', 'signingAuthority', 'status', hidden),
    col('agreements', 'invoicesOrganiser', 'status', hidden),
    col('agreements', 'settlementDays', 'integer', hidden),
    col('agreements', 'travelExpenses', 'status', hidden),
    col('agreements', 'commissionOnExpenses', 'boolean', hidden),
    col('agreements', 'noticeMonths', 'integer', hidden),
    col('agreements', 'sunsetMonths', 'integer', hidden),
    col('agreements', 'exclusivityRadiusKm', 'integer', hidden),
    col('agreements', 'exclusivityDays', 'integer', hidden),
    col('agreements', 'excludedWork', 'text', hidden),
    col('agreements', 'blackouts', 'multiline', hidden),
    col('agreements', 'signedBy', 'text', hidden),
    col('agreements', 'signedOn', 'date', hidden),
    col('agreements', 'specialTerms', 'multiline', hidden),
    col('agreements', 'updatedAt', 'date', hidden),
  ],
  landscape: true,
};

export const AGENCY_EXPORTS: readonly ExportMeta[] = [
  ARTISTS,
  ARTIST_PROFILE,
  ENQUIRIES,
  ARTIST_BOOKINGS,
  AGREEMENTS,
];
